// src/com/trans_plan_tsr/TransPlanTsrByStore.js
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/system';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import {
    ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord, TopCode } from '../../value/AppConst';
import {
    bulkCompanyProductsListRepo,transPlanTsrListParamRepo, transPlanTsrListRepo, userInfoRepo
} from '../../value/AppModel';
import { LIST_TRANS_PLAN_TSR } from './TransPlanTsrGql';
import { TransPlanTsrDictionary } from './TransPlanTsrDict';
import { useAlertSetter } from '../../api/app-hook';
import ValueMisc from '../../value/ValueMisc';
import TransPlanTsrItem from './TransPlanTsrItem';
import VzmDateField from '../../common/VzmDateField';
import IsValid from '../../value/IsValid';
import StoreHouseSvg from '../../asset/image/store_house.svg';
import {
    LIST_BULK_COMPANY_PRODUCTS
} from '../bulk_company_products/BulkCompanyProductsGql';

/**
 * (TRANS_PLAN_TSR) 정보 View Controller
 * VIEW
 * TSR 발행 점포용 화면.
 * 과거 프로젝트의 NM_StoreTSR
 * Using React with MUI5
 * Project: NewTOP for cloud
 */
export default function TransPlanTsrByStore() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const transPlanTsrListParam = useReactiveVar(transPlanTsrListParamRepo);
    const transPlanTsrListData = useReactiveVar(transPlanTsrListRepo);
    const bulkCompanyProductsListData = useReactiveVar(bulkCompanyProductsListRepo);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getTransPlanTsrList, responseList] = useLazyQuery(LIST_TRANS_PLAN_TSR, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: TransPlanTsrDictionary.errorTitle.Get, message: [error.message]})
    });
    // 982 Bulk 상품 목록 조회
    const [getBulkCompanyProductsList, responseBulkCompanyProductsList] = useLazyQuery(LIST_BULK_COMPANY_PRODUCTS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetBulkCompanyProductsList(data, option)},
		onError: (error) => addAlert({title: '발크상품 목록 조회 오류', message: [error.message]})
    });

    const getTransPlanTsrListNow = (dcCd, storeId, planDate) => {
        if(!planDate) {
            planDate = ValueMisc.getYMD();
        }
        const param = {variables:{dcCd:dcCd, storeId:storeId, planDate:planDate}};
        getTransPlanTsrList(param);
    };

    // 날짜, 점포 변경 시 TSR 조회 호출
    // 다른 화면에서 transPlanTsrListParam 설정하고 이 화면으로 들어오면 바로 조회 가능함.
    useEffect(() => {
        if(transPlanTsrListParam.dcCd && transPlanTsrListParam.storeId && transPlanTsrListParam.planDate) {
            getTransPlanTsrListNow(
                transPlanTsrListParam.dcCd, transPlanTsrListParam.storeId, transPlanTsrListParam.planDate);
        }
        else if(!transPlanTsrListParam.planDate) {
            const newParam = {...transPlanTsrListParam, planDate:ValueMisc.getYMD()};
            transPlanTsrListParamRepo(newParam);
        }
    }, [transPlanTsrListParam]);

    const getBulkCompanyProductsListNow = () => {
        const diff = new Date().getTime()/1000 - bulkCompanyProductsListData.when;
        if(diff > 3600) {
            getBulkCompanyProductsList({variables:{bulkCompanyId:null, bulkProductsId:null}});
        }
    };

    // 982 Bulk 상품 목록 조회
    useEffect(()=>{
        if(transPlanTsrListParam.dcCd===TopCode.DcCode.Adc982) {
            getBulkCompanyProductsListNow();
        }
    }, [transPlanTsrListParam]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        lastReloadRef.current = new Date().getTime()/1000;
        if(data.transPlanTsrList) {
            transPlanTsrListRepo({when:new Date().getTime()/1000, records:data.transPlanTsrList});
        }
    };

    // 982 Bulk 상품 목록 조회
    const onCompleteGetBulkCompanyProductsList = (data, clientOption) => {
        if(data.bulkCompanyProductsList) {
            bulkCompanyProductsListRepo({when:new Date().getTime()/1000, records:data.bulkCompanyProductsList});
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 2) {
            // Too short to reload
            enqueueSnackbar('2초 이내에 재조회는 지양해 주세요.', {variant:'info', autoHideDuration:2000});
        }
        else if(transPlanTsrListParam.dcCd && transPlanTsrListParam.storeId && transPlanTsrListParam.planDate) {
            getTransPlanTsrListNow(
                transPlanTsrListParam.dcCd, transPlanTsrListParam.storeId, transPlanTsrListParam.planDate);
        }
    };

    const onChangePlanDate = (id, value) => {
        const newParam = {...transPlanTsrListParam, planDate:value};
        transPlanTsrListParamRepo(newParam);
    };


    const loadingAny
        = responseList.loading // || responseItem.loading
        || responseBulkCompanyProductsList.loading;

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <VzmDateField
                        dict={{
                            id: 'planDate',
                            label: '출고일자'
                        }}
                        value={transPlanTsrListParam.planDate || ''}
                        error={!IsValid.date(transPlanTsrListParam.planDate)}
                        onChange={onChangePlanDate}
                    />
                    &nbsp;&nbsp;
                    <span>
                        {sessionInfo.user.dscName}
                    </span>
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <ContentBody style={{
                    paddingLeft:5, whiteSpace: 'nowrap', overflowX: 'auto'
                }}
            >
                {
                    transPlanTsrListData.records.map(item=>{
                        return(
                            <TransPlanTsrItem key={item.hocha}
                                item={item}
                                onCompleteMutation={()=>{return;}}
                            />
                        )
                    })
                }
                    {
                        transPlanTsrListData.records.length > 0 &&
                        transPlanTsrListData.records.length < 5 &&
                        Array.from(Array(5 - transPlanTsrListData.records.length))
                        .map((_, index) => {
                            return(
                                <div key={index}
                                    style={{
                                        display:'inline-flex',
                                        flexDirection: 'column',
                                        width: 220,
                                        textAlign:'center',
                                        alignItems:'center',
                                        color: AppStyle.PrimaryRGB,
                                        opacity: 0.5,
                                    }}
                                >
                                    <div style={{height: 250,}}>&nbsp;</div>
                                    <LocalShippingIcon
                                        fontSize={AppWord.LARGE}
                                    />
                                    <div
                                        style={{
                                            height: 10,
                                            borderRadius: 5,
                                            backgroundColor: AppStyle.PrimaryRGB,
                                            opacity: 0.3,
                                            width: '90%'
                                        }}
                                    >&nbsp;</div>
                                </div>
                            )
                        })
                    }
                {
                    transPlanTsrListData.records.length === 0 &&
                    <Box style={{
                        display:'flex',
                        flexDirection:'column',
                        justifyContent:'center',
                        alignItems:'center',
                        paddingTop:50
                    }}>
                        <img src={StoreHouseSvg} alt="_" height="320" />
                        <div style={{marginTop:20, fontSize:'2rem', color:'#fa3'}}>
                            우리 점포로의 배차내역이 없습니다.
                        </div>
                    </Box>
                }
            </ContentBody>
        </ContentConainer>
    );
}
